import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Image,
  Link,
  List,
  ListItem,
  Switch,
  Text,
  useBoolean,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../../Store";
import { formatDate } from "../../../utils/formatDate";
import { DetailsAccordion } from "../../Accordion/DetailsAccordion";
import { BadgePill } from "../../BadgePill/BadgePill";
import BackLink from "../../Controls/BackLink";
import Section from "../../Controls/Sections";
import {
  DataDeletionPopUp,
  DeIdentificationPopUp,
  OutsourcedProvidersPopUp,
} from "../PopUps";
import ConfirmationOfConsent from "./ConfirmationOfConsent";
import {
  IndividualConsentData,
  useGetIndividualConsentData,
  useUpdateConsent,
} from "./DashboardLanding/ManagePortalHooks";
import { ActionModal } from "../../Controls/Modal";
import ManagementPortalContainer from "./ManagementPortalContainer";

export default function DataSharingArrangement() {
  const navigate = useNavigate();

  const { consentId } = useParams();

  const { setLoading, setShowPopup, setShowPopupError, setPopupErrorMessage } =
    useStore();

  const [consentData, setConsentData] = useState<IndividualConsentData>();
  const GetManagePortalData = useGetIndividualConsentData(
    setConsentData,
    HandleApiError,
    consentId
  );

  const { deleteMyDataConsentMutation } = useUpdateConsent(
    GetManagePortalData,
    HandleApiError
  );

  useEffect(() => {
    setLoading(false);
    if (!consentId) {
      window.location.href = "/DashboardLanding";
    }
    GetManagePortalData();
  }, []);

  const [popupOutConfirmationOfConsent, setPopUpConfirmationOfConsent] =
    useState(false);

  const [popupOutsourcedProviders, setPopUpOutsourcedProviders] =
    useBoolean(false);
  const [popupDeIdentification, setPopupDeIdentification] = useBoolean(false);
  const [popupDataDeletion, setPopupDataDeletion] = useBoolean(false);

  const consent = consentData?.consent.consent;
  const dataHolderBrand = consentData?.consent.dataHolderBrand;

  const softwareProduct = consentData?.softwareProduct.softwareProduct;
  const softwareProductWording =
    consentData?.softwareProductWording?.softwareProductWording;

  const dataRecipient = softwareProduct?.dataRecipientBrand?.dataRecipient;

  let startTime = formatDate(consent?.startTime);
  let endTime = formatDate(consent?.endTime);
  let withdrawnTime = formatDate(consent?.withdrawnTime ?? "");

  const hasAdditionalUses =
    Boolean(consent?.additionalUses) &&
    consent!.additionalUses!.length > 0 &&
    consent!.additionalUses[0] !== "";
  const hasSoftwareProductWordingThirdParties =
    Boolean(softwareProductWording?.softwareProductWordingThirdParties) &&
    softwareProductWording!.softwareProductWordingThirdParties!.length > 0;

  function HandleApiError(message: string) {
    setLoading(false);
    setShowPopup(false);
    setShowPopupError(true);
    setPopupErrorMessage(message);
  }

  return (
    <>
      <ManagementPortalContainer>
        <BackLink page="/DashboardLanding" />

        <Heading variant="pageHeading">Data Sharing Arrangement</Heading>

        <Divider my={3} />

        <HStack my={1}>
          <Box mr={3}>
            <Image
              maxH={"100px"}
              maxW={"100px"}
              src={dataHolderBrand?.logoUri}
              alt="Data holder Logo"
              fallbackSrc="https://via.placeholder.com/150"
            />
          </Box>
          <Box flex={1}>
            <Text fontWeight="bold" mb={1}>
              {dataHolderBrand?.brandName}
            </Text>
            <Text>Product: {softwareProduct?.softwareProductName}</Text>
          </Box>

          <Box>
            {consent?.status.toLowerCase() === "active" ? (
              <BadgePill label="Active" status="success" />
            ) : consent?.status.toLowerCase() === "pending" ? (
              <BadgePill label="Pending" status="warning" />
            ) : (
              <BadgePill label="Archived" status="secondary" />
            )}
          </Box>
        </HStack>

        <Box>
          <ConfirmationOfConsent
            showPopup={popupOutConfirmationOfConsent}
            handleClose={() => setPopUpConfirmationOfConsent(false)}
            consent={consent}
            dataHolderBrand={dataHolderBrand}
            softwareProductWording={softwareProductWording}
          />
          <HStack mt={2} mb={4} justifyContent="space-between">
            <Button
              mt={2}
              variant="link"
              onClick={() => {
                setPopUpConfirmationOfConsent(true);
              }}
            >
              View your confirmation of consent
            </Button>

            {Boolean(
              consent?.status.toLowerCase() !== "withdrawn" &&
                consent?.status.toLowerCase() !== "expired"
            ) && (
              <Button
                onClick={() => navigate(`/ReviewWithdraw/${consentId}`)}
                variant="secondary"
              >
                {consent?.status.toLowerCase() === "active"
                  ? "Stop sharing"
                  : "Cancel sharing request"}
              </Button>
            )}
          </HStack>

          <Divider />

          <Heading as="h2" fontSize="md" mt={5}>
            Data we were collecting
          </Heading>

          {/* Consents */}

          {/* Individual */}
          {consent?.customerType === "Individual" && (
            <>
              {/* Name and occupation */}
              {consent?.nameAndOccupationConsent === "RequestedAndConsented" &&
                consent?.nameOccupationContactDetailsConsent !==
                  "RequestedAndConsented" && (
                  <DetailsAccordion
                    title="Name and occupation"
                    id="accordionNameOccupation"
                  >
                    <Heading size="sm">Why we need it</Heading>
                    <Text>
                      {softwareProductWording?.whyWeNeedNameAndOccupation}
                    </Text>

                    <Heading size="sm">What we are collecting</Heading>
                    <List>
                      <ListItem>Name</ListItem>
                      <ListItem>Occupation</ListItem>
                    </List>

                    <Heading size="sm">When we've collected your data</Heading>
                    <Text>
                      We first collected your transaction details from{" "}
                      {dataHolderBrand?.brandName} on {startTime}.<br />
                      We'll continue to collect this everytime you use{" "}
                      {softwareProduct?.softwareProductName} until {endTime}.
                    </Text>
                  </DetailsAccordion>
                )}

              {/* Contact details  */}
              {consent?.contactDetailsConsent === "RequestedAndConsented" &&
                consent?.nameOccupationContactDetailsConsent !==
                  "RequestedAndConsented" && (
                  <DetailsAccordion
                    title="Contact details"
                    id="accordionContactDetails"
                  >
                    <h5>Why we need it</h5>
                    <p>{softwareProductWording?.whyWeNeedContactDetails}</p>

                    <h5>What we are collecting</h5>
                    <List>
                      <ListItem>Phone</ListItem>
                      <ListItem>Email address</ListItem>
                      <ListItem>Mail address</ListItem>
                      <ListItem>Residential address</ListItem>
                    </List>

                    <hr />

                    <h5>When we've collected your data</h5>
                    <p>
                      We first collected your transaction details from{" "}
                      {dataHolderBrand?.brandName} on {startTime}.<br />
                      We'll continue to collect this everytime you use{" "}
                      {softwareProduct?.softwareProductName} until {endTime}.
                    </p>
                  </DetailsAccordion>
                )}

              {/* Name, occupation and contact details  */}
              {consent?.nameOccupationContactDetailsConsent ===
                "RequestedAndConsented" && (
                <DetailsAccordion
                  id="accordionNameOccupationContactDetails"
                  title="Name, occupation and contact details"
                >
                  <Heading fontSize="sm">Why we need it</Heading>
                  <Text>
                    {
                      softwareProductWording?.whyWeNeedNameOccupationContactDetails
                    }
                  </Text>

                  <Heading fontSize="sm">What we are collecting</Heading>
                  <List>
                    <ListItem>Name</ListItem>
                    <ListItem>Occupation</ListItem>
                    <ListItem>Phone</ListItem>
                    <ListItem>Email address</ListItem>
                    <ListItem>Mail address</ListItem>
                    <ListItem>Residential address</ListItem>
                  </List>

                  <Heading fontSize="sm">
                    When we've collected your data
                  </Heading>
                  <Text>
                    We first collected your transaction details from{" "}
                    {dataHolderBrand?.brandName} on {startTime}.<br />
                    We'll continue to collect this everytime you use{" "}
                    {softwareProduct?.softwareProductName} until {endTime}.
                  </Text>
                </DetailsAccordion>
              )}
            </>
          )}

          {/* Business */}
          {consent?.customerType === "Business" ? (
            <>
              {/* Organisation profile   */}
              {consent?.organisationProfileConsent ===
                "RequestedAndConsented" &&
                consent?.organisationProfileAndContactDetailsConsent !==
                  "RequestedAndConsented" && (
                  <DetailsAccordion
                    id="accordionOrganisationProfile"
                    title="Organisation profile"
                  >
                    <Heading fontSize="sm">Why we need it</Heading>
                    <Text>
                      {softwareProductWording?.whyWeNeedOrganisationProfile}
                    </Text>

                    <Heading fontSize="sm">What we are collecting</Heading>
                    <List>
                      <ListItem>Agent name and role</ListItem>
                      <ListItem>Organisation name</ListItem>
                      <ListItem>Organisation numbers (ABN or ACN)</ListItem>
                      <ListItem>Charity status</ListItem>
                      <ListItem>Establishment date</ListItem>
                      <ListItem>Industry</ListItem>
                      <ListItem>Organisation type</ListItem>
                      <ListItem>Country of registration</ListItem>
                    </List>

                    <Heading fontSize="sm">
                      When we've collected your data
                    </Heading>
                    <Text>
                      We first collected your transaction details from{" "}
                      {dataHolderBrand?.brandName} on {startTime}.<br />
                      We'll continue to collect this everytime you use{" "}
                      {softwareProduct?.softwareProductName} until {endTime}.
                    </Text>
                  </DetailsAccordion>
                )}

              {/* Organisation contact details  */}
              {consent?.organisationContactDetailsConsent ===
                "RequestedAndConsented" &&
                consent?.organisationProfileAndContactDetailsConsent !==
                  "RequestedAndConsented" && (
                  <DetailsAccordion
                    id="headingOrganisationContactDetails"
                    title="Organisation contact details"
                  >
                    <Heading fontSize="sm">Why we need it</Heading>
                    <Text>
                      {
                        softwareProductWording?.whyWeNeedOrganisationContactDetails
                      }
                    </Text>

                    <Heading fontSize="sm">What we are collecting</Heading>
                    <List>
                      <ListItem>Organisation address</ListItem>
                      <ListItem>Mail address</ListItem>
                      <ListItem>Phone number</ListItem>
                    </List>

                    <Heading fontSize="sm">
                      When we've collected your data
                    </Heading>
                    <Text>
                      We first collected your transaction details from{" "}
                      {dataHolderBrand?.brandName} on {startTime}.<br />
                      We'll continue to collect this everytime you use{" "}
                      {softwareProduct?.softwareProductName} until {endTime}.
                    </Text>
                  </DetailsAccordion>
                )}

              {/* Organisation profile and contact details  */}
              {consent?.organisationProfileAndContactDetailsConsent ===
                "RequestedAndConsented" && (
                <DetailsAccordion
                  id="accordionOrgProfileContactDetails"
                  title="Organisation profile and contact details"
                >
                  <Heading fontSize="sm">Why we need it</Heading>
                  <Text>
                    {
                      softwareProductWording?.whyWeNeedOrganisationProfileAndContactDetails
                    }
                  </Text>

                  <Heading fontSize="sm">What we are collecting</Heading>
                  <List>
                    <ListItem>Agent name and role</ListItem>
                    <ListItem>Organisation name</ListItem>
                    <ListItem>Organisation numbers (ABN or ACN)</ListItem>
                    <ListItem>Charity status</ListItem>
                    <ListItem>Establishment date</ListItem>
                    <ListItem>Industry</ListItem>
                    <ListItem>Organisation type</ListItem>
                    <ListItem>Country of registration</ListItem>
                    <ListItem>Organisation address</ListItem>
                    <ListItem>Mail address</ListItem>
                    <ListItem>Phone number</ListItem>
                  </List>

                  <Heading fontSize="sm">
                    When we've collected your data
                  </Heading>
                  <Text>
                    We first collected your transaction details from{" "}
                    {dataHolderBrand?.brandName} on {startTime}.<br />
                    We'll continue to collect this everytime you use{" "}
                    {softwareProduct?.softwareProductName} until {endTime}.
                  </Text>
                </DetailsAccordion>
              )}
            </>
          ) : null}

          {/* Account name, type and balance  */}
          {consent?.accountNameTypeAndBalanceConsent ===
            "RequestedAndConsented" &&
            consent?.accountBalanceAndDetailsConsent !==
              "RequestedAndConsented" && (
              <DetailsAccordion
                id="accordionAccountTypeBalance"
                title="Account name, type and balance"
              >
                <Heading fontSize="sm">Why we need it</Heading>
                <Text>
                  {softwareProductWording?.whyWeNeedAccountNameTypeAndBalance}
                </Text>

                <Heading fontSize="sm">What we are collecting</Heading>
                <List>
                  <ListItem>Name of account</ListItem>
                  <ListItem>Type of account</ListItem>
                  <ListItem>Account balance</ListItem>
                </List>

                <Heading fontSize="sm">When we've collected your data</Heading>
                <Text>
                  We first collected your transaction details from{" "}
                  {dataHolderBrand?.brandName} on {startTime}.<br />
                  We'll continue to collect this everytime you use{" "}
                  {softwareProduct?.softwareProductName} until {endTime}.
                </Text>
              </DetailsAccordion>
            )}

          {/* Account numbers and features  */}
          {consent?.accountNumbersAndFeaturesConsent ===
            "RequestedAndConsented" &&
            consent?.accountBalanceAndDetailsConsent !==
              "RequestedAndConsented" && (
              <DetailsAccordion
                id="accordionAccountFeatures"
                title="Account numbers and features"
              >
                <Heading fontSize="sm">Why we need it</Heading>
                <Text>
                  {softwareProductWording?.whyWeNeedAccountNumbersAndFeatures}
                </Text>

                <Heading fontSize="sm">What we are collecting</Heading>
                <List>
                  <ListItem>Account number</ListItem>
                  <ListItem>Interest rates</ListItem>
                  <ListItem>Fees</ListItem>
                  <ListItem>Discounts</ListItem>
                  <ListItem>Account terms</ListItem>
                  <ListItem>Account mail address</ListItem>
                </List>

                <Heading fontSize="sm">When we've collected your data</Heading>
                <Text>
                  We first collected your transaction details from{" "}
                  {dataHolderBrand?.brandName} on {startTime}.<br />
                  We'll continue to collect this everytime you use{" "}
                  {softwareProduct?.softwareProductName} until {endTime}.
                </Text>
              </DetailsAccordion>
            )}

          {/* Account balance and details */}
          {consent?.accountBalanceAndDetailsConsent ===
            "RequestedAndConsented" && (
            <DetailsAccordion
              id="accordionAccountBalanceDetails"
              title="Account balance and details"
            >
              <Heading fontSize="sm">Why we need it</Heading>
              <Text>
                {softwareProductWording?.whyWeNeedAccountBalanceAndDetails}
              </Text>

              <Heading fontSize="sm">What we are collecting</Heading>
              <List>
                <ListItem>Name of account</ListItem>
                <ListItem>Type of account</ListItem>
                <ListItem>Account balance</ListItem>
                <ListItem>Account number</ListItem>
                <ListItem>Interest rates</ListItem>
                <ListItem>Fees</ListItem>
                <ListItem>Discounts</ListItem>
                <ListItem>Account terms</ListItem>
                <ListItem>Account mail address</ListItem>
              </List>

              <Heading fontSize="sm">When we've collected your data</Heading>
              <Text>
                We first collected your transaction details from{" "}
                {dataHolderBrand?.brandName} on {startTime}.<br />
                We'll continue to collect this everytime you use{" "}
                {softwareProduct?.softwareProductName} until {endTime}.
              </Text>
            </DetailsAccordion>
          )}

          {/* Transaction details */}
          {consent?.transactionDetailsConsent === "RequestedAndConsented" && (
            <DetailsAccordion
              id="accordionTransactionDetails"
              title="Transaction details"
            >
              <Heading fontWeight="sm">Why we need it</Heading>
              <Text>{softwareProductWording?.whyWeNeedTransactionDetails}</Text>

              <Heading fontWeight="sm">What we are collecting</Heading>
              <List>
                <ListItem>Incoming and outgoing transactions</ListItem>
                <ListItem>Amounts</ListItem>
                <ListItem>Dates</ListItem>
                <ListItem>Descriptions of transactions</ListItem>
                <ListItem>
                  Who you've sent money to and recieved from (e.g. their name)
                </ListItem>
              </List>

              <Heading fontWeight="sm">Historical data we've collected</Heading>
              <Text>
                We have collected transaction data that may date back to 1st
                January 2017.
              </Text>

              <Heading fontWeight="sm">When we've collected your data</Heading>
              <Text>
                We first collected your transaction details from{" "}
                {dataHolderBrand?.brandName} on {startTime}.<br />
                We'll continue to collect this everytime you use{" "}
                {softwareProduct?.softwareProductName} until {endTime}.
              </Text>
            </DetailsAccordion>
          )}

          {/* Direct debits and scheduled payments */}
          {consent?.directDebitsAndScheduledPaymentsConsent ===
            "RequestedAndConsented" && (
            <DetailsAccordion
              id="accordionDirectDebits"
              title="Direct debits and scheduled payments"
            >
              <Heading fontSize="sm">Why we need it</Heading>
              <Text>
                {
                  softwareProductWording?.whyWeNeedDirectDebitsAndScheduledPayments
                }
              </Text>

              <Heading fontSize="sm">What we are collecting</Heading>
              <List>
                <ListItem>Direct debits</ListItem>
                <ListItem>Scheduled payments</ListItem>
              </List>

              <Heading fontSize="sm">When we've collected your data</Heading>
              <Text>
                We first collected your transaction details from{" "}
                {dataHolderBrand?.brandName} on {startTime}.<br />
                We'll continue to collect this everytime you use{" "}
                {softwareProduct?.softwareProductName} until {endTime}.
              </Text>
            </DetailsAccordion>
          )}

          {/* Saved payees */}
          {consent?.savedPayeesConsent === "RequestedAndConsented" && (
            <DetailsAccordion id="accordionSavedPayees" title="Saved payees">
              <Heading fontSize="sm">Why we need it</Heading>
              <Text>{softwareProductWording?.whyWeNeedSavedPayees}</Text>

              <Heading fontSize="sm">What we are collecting</Heading>
              <List>
                <ListItem>
                  Names and details of accounts you have saved (e.g. their BSB
                  and Account Number, BPay CRN and Biller code, or NPP PayID)
                </ListItem>
              </List>

              <Heading fontSize="sm">When we've collected your data</Heading>
              <Text>
                We first collected your transaction details from{" "}
                {dataHolderBrand?.brandName} on {startTime}.<br />
                We'll continue to collect this everytime you use{" "}
                {softwareProduct?.softwareProductName} until {endTime}.
              </Text>
            </DetailsAccordion>
          )}
        </Box>

        <Section>
          <Box>
            <DetailsAccordion title="Key dates">
              <Heading size="sm">When you gave consent: </Heading>

              <Text>{startTime}</Text>

              {consent?.status.toLowerCase() !== "expired" &&
              consent?.status.toLowerCase() !== "withdrawn" ? (
                <>
                  <Heading size="sm">When your consent will expire: </Heading>
                  <Text>{endTime}</Text>
                </>
              ) : consent?.status.toLowerCase() === "expired" ? (
                <>
                  <Heading size="sm">When your consent expired: </Heading>
                  <Text>{withdrawnTime}</Text>
                </>
              ) : (
                <>
                  <Heading size="sm">When your consent was withdrawn: </Heading>
                  <Text>{withdrawnTime}</Text>
                </>
              )}

              <Heading size="sm">Sharing period: </Heading>
              <p>
                {startTime} - {withdrawnTime !== null ? withdrawnTime : endTime}
              </p>
              <Text>
                {startTime} - {endTime}
              </Text>

              {consent?.status.toLowerCase() !== "pending" ? (
                <>
                  <Heading size="sm">How often we access your data: </Heading>
                  {consent?.isOneOffConsent === true ? (
                    <Text>Your data was accessed once</Text>
                  ) : (
                    <Text>
                      We do this everytime you log into{" "}
                      <Text as="span" fontWeight="bold">
                        {dataRecipient?.legalEntityName}
                      </Text>
                    </Text>
                  )}
                </>
              ) : null}
            </DetailsAccordion>
          </Box>

          {Boolean(consent?.accounts) &&
            Boolean(consent!.accounts!.length > 0) && (
              <Box>
                <DetailsAccordion title="Accounts">
                  {consent?.accounts?.map((a: any, index: any) => (
                    <div key={index}>
                      <Heading fontSize="sm">{a?.name}</Heading>
                      <Text>{a?.maskedAccountNumber}</Text>
                    </div>
                  ))}
                </DetailsAccordion>
              </Box>
            )}

          {Boolean(
            hasAdditionalUses || hasSoftwareProductWordingThirdParties
          ) && (
            <Box>
              <DetailsAccordion title="Additional uses of your data">
                {hasAdditionalUses ? (
                  <>
                    <Text>You have agreed to let us do the following:</Text>

                    <List>
                      {(
                        softwareProductWording?.softwareProductWordingAdditionalUses ||
                        []
                      )
                        .filter((a: any) =>
                          consent?.additionalUses.includes(a.id)
                        )
                        .map((au: any) => (
                          <ListItem key={au.id}>{au.description}</ListItem>
                        ))}
                    </List>
                  </>
                ) : null}

                {Boolean(
                  (
                    softwareProductWording?.softwareProductWordingThirdParties ||
                    []
                  ).length > 0
                ) ? (
                  <>
                    <Heading fontSize="sm">Supporting third parties</Heading>

                    <Text>
                      Supporting third parties will help provide this service.
                      This will be bound by the same terms of this agreement.
                    </Text>

                    <OutsourcedProvidersPopUp
                      showPopup={popupOutsourcedProviders}
                      handleClose={setPopUpOutsourcedProviders.off}
                    />
                    <Button
                      mt={2}
                      variant="link"
                      onClick={setPopUpOutsourcedProviders.on}
                    >
                      See list of providers
                    </Button>
                  </>
                ) : null}
              </DetailsAccordion>
            </Box>
          )}

          <Box>
            <DetailsAccordion title="Your data when we no longer require it">
              {softwareProductWording?.canDeIdentifyData ? (
                <>
                  <Heading fontSize={"sm"}>De-identification of data</Heading>
                  <Text>
                    {softwareProductWording.paragraphDataAfterItIsUsed}
                  </Text>
                  <DeIdentificationPopUp
                    showPopup={popupDeIdentification}
                    handleClose={setPopupDeIdentification.off}
                  />
                  <Button
                    mt={2}
                    variant="link"
                    onClick={setPopupDeIdentification.on}
                  >
                    How and why we de-identify your data
                  </Button>
                  <HStack my={5} justifyContent="space-between">
                    <Heading fontSize="sm">Delete my data instead</Heading>
                    <Switch
                      size="md"
                      aria-labelledby="Delete My Data"
                      isChecked={Boolean(consent?.deleteMyData)}
                      onChange={() => {
                        deleteMyDataConsentMutation(consentId, consent);
                      }}
                    />
                  </HStack>
                  <Text>
                    If you don't do this before {endTime}, your data will be
                    de-identified.
                  </Text>
                </>
              ) : (
                <>
                  {consent?.status.toLowerCase() !== "expired" ||
                  consent?.status.toLowerCase() !== "withdrawn" ? (
                    <>
                      <Text>All your data will be deleted.</Text>
                    </>
                  ) : (
                    <Text>All your data was deleted on {withdrawnTime}.</Text>
                  )}
                </>
              )}

              <DataDeletionPopUp
                showPopup={popupDataDeletion}
                handleClose={setPopupDataDeletion.off}
              />

              <Button mt={2} variant="link" onClick={setPopupDataDeletion.on}>
                See how we delete your data
              </Button>
            </DetailsAccordion>
          </Box>
        </Section>
        <Section isGrey={true}>
          <Text>
            Please read our{" "}
            <Link
              href={dataRecipient?.consumerDataRightsPolicyUri}
              aria-label={
                "Link To " + dataRecipient?.consumerDataRightsPolicyUri
              }
              target="_blank"
              rel="noreferrer"
            >
              Consumer Data Right policy
            </Link>{" "}
            for more information on how <b>{dataRecipient?.legalEntityName}</b>{" "}
            handles your data.
          </Text>
        </Section>
      </ManagementPortalContainer>
    </>
  );
}
